// The root card at the top of the Dhātu lab's result column: the root's picture, the root, its अन्य पुरुष एकवचन present form, the
// meaning, its class, the book it comes from and a link to the root's dictionary page.

import type { FC, ReactNode } from 'react'
import type { Tense } from '@/prakrit/lib/grammar/types'
import type { LabRoot } from '@/prakrit/components/grammar/labs/verb-data'

import Link from 'next/link'
import { ArrowRight, BookOpen } from 'lucide-react'
import { cn } from '@/lib/utils'
import { SOURCE } from '@/prakrit/lib/grammar/lexicon'
import { Bi } from '@/prakrit/components/common/bi'
import { DhatuPicture } from '@/prakrit/components/common/dhatu-picture'
import { StatusBadge, reviewBadgeKind } from '@/prakrit/components/common/status-badge'
import { vocabHref } from '@/prakrit/components/vocabulary/vocab-href'
import { hiDigits } from '@/prakrit/components/grammar/labs/lab-ui'
import { tenseNote } from '@/prakrit/components/grammar/labs/verb-data'
import { PANEL } from '@/prakrit/components/grammar/dhatu/dhatu-ui'
import { rootClassLabel } from '@/prakrit/components/grammar/dhatu/labels'
import { coreForm } from '@/prakrit/components/grammar/dhatu/meaning'

interface RootHeaderProps {
    root: LabRoot
    tense: Tense
    /** Review state of the shown table (the badge hides itself from learners). */
    review: string
    className?: string
}

const BADGE = 'inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs leading-none'

export const RootHeader: FC<RootHeaderProps> = ({ root, tense, review, className }): ReactNode => {
    const present = coreForm(root, 'vartaman', 'anya', 'eka')
    const cls = rootClassLabel(root)
    const tables = Object.keys(root.tables).length
    const rule = tenseNote(tense, root.group).rule

    return (
        <section aria-labelledby='root-title' className={cn(PANEL, 'flex flex-col gap-4 p-5 sm:flex-row sm:items-start sm:gap-6 sm:p-6', className)}>
            <DhatuPicture
                key={root.root}
                root={root.root}
                meaning={`${root.hi} · ${root.en}`}
                sizes='(min-width: 640px) 176px, 60vw'
                priority
                className='w-full max-w-60 self-center rounded-3xl shadow-card motion-safe:animate-in motion-safe:fade-in motion-safe:zoom-in-95 motion-safe:duration-500 sm:w-44 sm:shrink-0 sm:self-start'
            />
            <div className='flex min-w-0 flex-1 flex-col gap-3'>
                <div className='flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between'>
                    <h2 id='root-title' className='flex flex-wrap items-baseline gap-x-2 font-sans'>
                        <span lang='pra' className='pra pra-display'>{root.root}</span>
                        {present && (
                            <>
                                <span aria-hidden='true' className='text-2xl text-muted-foreground'>·</span>
                                <span lang='pra' className='font-prakrit text-2xl font-medium text-muted-foreground'>{present}</span>
                                <span className='sr-only'>(अन्य पुरुष एकवचन, वर्तमान काल · third person singular, present)</span>
                            </>
                        )}
                    </h2>
                    <div className='flex flex-wrap items-center gap-1.5 sm:justify-end'>
                        <span className={cn(BADGE, 'bg-prakrit/8 text-prakrit')}>
                            <span lang='hi' className='hindi font-semibold'>{cls.hi}</span>
                            <span lang='en' data-ex='en' className='opacity-75'>{cls.en}</span>
                        </span>
                        <span className={cn(BADGE, 'bg-saffron/15 text-saffron-strong')}>
                            <span lang='hi' className='hindi font-semibold'>{root.group === 'as' ? 'अनियमित रूप' : 'नियम से बने रूप'}</span>
                            <span lang='en' data-ex='en' className='opacity-80'>{root.group === 'as' ? 'irregular' : 'built by rule'}</span>
                        </span>
                        <span className={cn(BADGE, 'bg-secondary text-foreground')}>
                            <span lang='hi' className='hindi font-semibold'>{hiDigits(tables)} तालिकाएँ</span>
                            <span lang='en' data-ex='en' className='text-muted-foreground'>{tables} tables</span>
                        </span>
                        <StatusBadge kind={reviewBadgeKind(review)} />
                    </div>
                </div>

                <Bi hi={root.hi} en={root.en} layout='inline' className='text-lg' enClassName='text-muted-foreground' />

                <p className='text-sm text-muted-foreground'>
                    <span lang='hi' className='hindi'>स्रोत</span>
                    <span aria-hidden='true'> · </span>
                    <span lang='en'>Source: </span>
                    <span lang='hi' className='hindi'>{SOURCE}, {rule}</span>
                </p>

                <Link
                    href={vocabHref(root.root)}
                    className='group inline-flex min-h-tap items-center gap-2 self-start rounded-full text-sm font-semibold text-saffron-strong hover:underline'
                >
                    <BookOpen className='size-4' aria-hidden='true' />
                    <span lang='hi' className='hindi'>इस धातु का पूरा पृष्ठ</span>
                    <span aria-hidden='true'>·</span>
                    <span lang='en'>Full page for this root</span>
                    <ArrowRight className='size-4 transition-transform group-hover:translate-x-0.5' aria-hidden='true' />
                </Link>
            </div>
        </section>
    )
}
